import { useEffect, useMemo, useState, type CSSProperties, type ReactNode } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Button, Card, Pill, fmtBRL } from "@atlas/ui/web";
import type { ContratoRefinanciavel, Refinanciamento } from "@atlas/sdk";
import { atlas } from "../../lib/sdk";
import { mensagemDeErro } from "../../lib/erro-validacao";
import { MatriculaInfo, readActiveMatricula, hydrateMatriculas, STORAGE_KEY_ID, STORAGE_KEY_META } from "../../lib/matricula-data";
import {
  ESTADO_REFIN_LABEL,
  acoesDoRefin,
  alternarOrigem,
  fmtDataHora,
  fmtTaxaFracao,
  pillDoEstadoRefin,
  refinEmAberto,
} from "../../lib/refinanciamento";
import { ImpersonateReadOnlyNotice, useIsImpersonating } from "../../components/ImpersonateBar";

const inputStyle: CSSProperties = {
  padding: "9px 12px",
  borderRadius: 8,
  border: "1px solid var(--border-strong)",
  background: "var(--bg-elev-2)",
  color: "var(--text)",
  fontSize: 14,
  fontFamily: "inherit",
  boxSizing: "border-box",
  width: "100%",
};

/** Refinanciamento com o proprio credor: o servidor pede, o banco propoe, o
 *  servidor aceita — e o contrato novo quita os antigos na folha. */
export function ServidorRefinanciamento() {
  const [sp] = useSearchParams();
  const bancoFiltro = (sp.get("banco") ?? "").toLowerCase();
  const adfInicial = sp.get("adf") ?? "";
  const qc = useQueryClient();
  const impersonando = useIsImpersonating();
  const [info, setInfo] = useState<MatriculaInfo | null>(() => readActiveMatricula());
  const [exigeSenha, setExigeSenha] = useState(() => readActiveMatricula()?.exigeSenhaTransacao === true);

  useEffect(() => {
    void hydrateMatriculas().then(() => {
      setInfo(readActiveMatricula());
      setExigeSenha(readActiveMatricula()?.exigeSenhaTransacao === true);
    });
    const onStorage = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY_META || e.key === STORAGE_KEY_ID) setInfo(readActiveMatricula());
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  const matricula = info?.matricula;
  const meusQ = useQuery({
    queryKey: ["servidor", "refinanciamentos", matricula],
    queryFn: () => atlas.refinanciamento.meus(matricula),
    enabled: !!matricula,
    refetchInterval: 10_000,
    refetchOnMount: "always",
  });
  const elegiveisQ = useQuery({
    queryKey: ["servidor", "refinanciamentos", "elegiveis", matricula],
    queryFn: () => atlas.refinanciamento.elegiveis(matricula),
    enabled: !!matricula,
  });

  const invalidar = () => {
    void qc.invalidateQueries({ queryKey: ["servidor", "refinanciamentos"] });
    void qc.invalidateQueries({ queryKey: ["servidor", "propostas"] });
    void hydrateMatriculas().then(() => setInfo(readActiveMatricula()));
  };

  const meus = meusQ.data?.itens ?? [];
  const abertos = meus.filter((r) => refinEmAberto(r) || r.estado === "aceito");
  const encerrados = meus.filter((r) => !abertos.includes(r));

  const elegiveis = useMemo(() => {
    const todos = elegiveisQ.data?.itens ?? [];
    return bancoFiltro ? todos.filter((c) => c.bancoNome.toLowerCase() === bancoFiltro) : todos;
  }, [elegiveisQ.data, bancoFiltro]);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      <header>
        <span className="eyebrow">Refinanciamento</span>
        <h1 style={{ margin: "4px 0 0", fontSize: "1.6rem" }}>Renegociar com a sua instituição</h1>
        <p style={{ color: "var(--text-muted)", marginTop: 6, maxWidth: 720, lineHeight: 1.5 }}>
          Escolha contratos da mesma instituição e diga a parcela (ou o valor) e o prazo que você quer. A instituição responde com uma
          proposta; ao aceitar, um contrato novo quita os atuais e entra no lugar deles na folha. Para trocar de instituição, use a{" "}
          <Link to="/servidor/portabilidade" style={{ color: "var(--gold-500)" }}>portabilidade</Link>.
        </p>
      </header>

      {abertos.length > 0 ? (
        <section style={{ display: "grid", gap: 12 }}>
          <h2 style={{ margin: 0, fontSize: "1.1rem" }}>Em andamento</h2>
          {abertos.map((r) => (
            <RefinCard key={r.id} r={r} exigeSenha={exigeSenha} impersonando={impersonando} onMudou={invalidar} />
          ))}
        </section>
      ) : null}

      <NovoPedido
        key={matricula ?? "-"}
        matricula={matricula}
        elegiveis={elegiveis}
        carregando={elegiveisQ.isLoading}
        erro={elegiveisQ.error ? mensagemDeErro(elegiveisQ.error, "Não foi possível carregar seus contratos.") : null}
        adfInicial={adfInicial}
        exigeSenha={exigeSenha}
        impersonando={impersonando}
        onCriado={invalidar}
      />

      {encerrados.length > 0 ? (
        <section style={{ display: "grid", gap: 12 }}>
          <h2 style={{ margin: 0, fontSize: "1.1rem" }}>Histórico</h2>
          {encerrados.map((r) => (
            <RefinCard key={r.id} r={r} exigeSenha={exigeSenha} impersonando={impersonando} onMudou={invalidar} />
          ))}
        </section>
      ) : null}
    </div>
  );
}

function NovoPedido({
  matricula,
  elegiveis,
  carregando,
  erro,
  adfInicial,
  exigeSenha,
  impersonando,
  onCriado,
}: {
  matricula: string | undefined;
  elegiveis: ContratoRefinanciavel[];
  carregando: boolean;
  erro: string | null;
  adfInicial: string;
  exigeSenha: boolean;
  impersonando: boolean;
  onCriado: () => void;
}) {
  const [selecionadas, setSelecionadas] = useState<string[]>([]);
  const [modo, setModo] = useState<"parcela" | "valor">("parcela");
  const [parcela, setParcela] = useState("");
  const [valor, setValor] = useState("");
  const [prazo, setPrazo] = useState("");
  const [senha, setSenha] = useState("");
  const [ok, setOk] = useState<string | null>(null);

  useEffect(() => {
    if (!adfInicial || selecionadas.length > 0) return;
    if (elegiveis.some((c) => c.adf === adfInicial && c.elegivel)) setSelecionadas([adfInicial]);
  }, [adfInicial, elegiveis, selecionadas.length]);

  const escolhidos = elegiveis.filter((c) => selecionadas.includes(c.adf));
  const somaParcelas = escolhidos.reduce((s, c) => s + c.valorParcela, 0);
  const somaSaldos = escolhidos.reduce((s, c) => s + c.saldoDevedor, 0);
  const banco = escolhidos[0];

  const parcelaNum = Number(parcela.replace(",", "."));
  const valorNum = Number(valor.replace(",", "."));
  const prazoNum = Number(prazo);
  const formularioOk =
    escolhidos.length > 0 &&
    Number.isInteger(prazoNum) && prazoNum >= 1 && prazoNum <= 120 &&
    (modo === "parcela" ? parcelaNum > 0 : valorNum > 0) &&
    (!exigeSenha || senha.length > 0);

  const mut = useMutation({
    mutationFn: () =>
      atlas.refinanciamento.solicitar({
        matricula,
        origens: selecionadas,
        parcelaSolicitada: modo === "parcela" ? parcelaNum : undefined,
        valorSolicitado: modo === "valor" ? valorNum : undefined,
        prazoSolicitado: prazoNum,
        senha: exigeSenha ? senha : undefined,
      }),
    onSuccess: (r) => {
      setOk(`Pedido ${r.id} enviado ao ${r.bancoNome}. Você recebe a proposta aqui.`);
      setSelecionadas([]);
      setParcela("");
      setValor("");
      setPrazo("");
      setSenha("");
      onCriado();
    },
  });

  return (
    <Card>
      <h2 style={{ margin: "0 0 4px", fontSize: "1.1rem" }}>Pedir refinanciamento</h2>
      <p style={{ margin: "0 0 14px", color: "var(--text-muted)", fontSize: ".9rem" }}>
        Só entram contratos ativos que o seu órgão ou entidade permite refinanciar.
      </p>
      {ok ? (
        <div role="status" style={{ marginBottom: 12, color: "var(--emerald-500)", fontWeight: 600 }}>{ok}</div>
      ) : null}
      {erro ? <p style={{ color: "var(--danger, #DC2626)" }}>{erro}</p> : null}
      {carregando ? <p style={{ color: "var(--text-muted)" }}>Carregando seus contratos…</p> : null}
      {!carregando && !erro && elegiveis.length === 0 ? (
        <p style={{ color: "var(--text-muted)", margin: 0 }}>Você não tem contratos ativos para refinanciar agora.</p>
      ) : null}

      <div style={{ display: "grid", gap: 10 }}>
        {elegiveis.map((c) => {
          const marcado = selecionadas.includes(c.adf);
          return (
            <label
              key={c.adf}
              style={{
                display: "flex",
                gap: 12,
                alignItems: "flex-start",
                padding: 12,
                borderRadius: 10,
                border: `1px solid ${marcado ? "var(--gold-500)" : "var(--border)"}`,
                cursor: c.elegivel ? "pointer" : "not-allowed",
                opacity: c.elegivel ? 1 : 0.6,
              }}
            >
              <input
                type="checkbox"
                checked={marcado}
                disabled={!c.elegivel}
                onChange={() => setSelecionadas((s) => alternarOrigem(s, c, elegiveis))}
                style={{ marginTop: 4 }}
              />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: "flex", justifyContent: "space-between", gap: 8, flexWrap: "wrap" }}>
                  <div>
                    <div style={{ fontWeight: 700 }}>{c.bancoNome}</div>
                    <div style={{ fontSize: 12, color: "var(--text-muted)", fontFamily: "var(--font-mono)" }}>
                      {c.adf} · {c.convenioNome}
                    </div>
                  </div>
                  <Pill variant={c.elegivel ? "emdia" : "expirado"}>{c.elegivel ? "Pode refinanciar" : "Indisponível"}</Pill>
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(130px, 1fr))", gap: 10, marginTop: 10, fontSize: 13 }}>
                  <KV label="Parcela" v={fmtBRL(c.valorParcela)} />
                  <KV label="Saldo devedor" v={fmtBRL(c.saldoDevedor)} />
                  <KV label="Pagas" v={`${c.parcelasPagas}/${c.totalParcelas}`} />
                  <KV label="Taxa" v={c.taxaAm > 0 ? fmtTaxaFracao(c.taxaAm) : "não informada"} />
                </div>
                {c.motivo ? <div style={{ marginTop: 8, fontSize: 12, color: "var(--gold-500)" }}>{c.motivo}</div> : null}
              </div>
            </label>
          );
        })}
      </div>

      {banco ? (
        <div style={{ marginTop: 16, display: "grid", gap: 12 }}>
          <div style={{ fontSize: ".9rem", color: "var(--text-muted)" }}>
            {escolhidos.length} contrato(s) com o <b style={{ color: "var(--text)" }}>{banco.bancoNome}</b> · parcelas somadas{" "}
            <b style={{ color: "var(--text)" }}>{fmtBRL(somaParcelas)}</b> · saldo devedor{" "}
            <b style={{ color: "var(--text)" }}>{fmtBRL(somaSaldos)}</b>
            {!banco.regras.permiteTroco ? " · este convênio não libera troco" : ""}
          </div>
          <div role="radiogroup" aria-label="O que você quer definir" style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <Button variant={modo === "parcela" ? undefined : "ghost"} onClick={() => setModo("parcela")}>Definir a parcela</Button>
            <Button variant={modo === "valor" ? undefined : "ghost"} onClick={() => setModo("valor")}>Definir o valor</Button>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 12 }}>
            {modo === "parcela" ? (
              <Campo label="Parcela desejada (R$)">
                <input style={inputStyle} inputMode="decimal" value={parcela} onChange={(e) => setParcela(e.target.value)} placeholder={somaParcelas.toFixed(2)} />
              </Campo>
            ) : (
              <Campo label="Valor desejado (R$)">
                <input style={inputStyle} inputMode="decimal" value={valor} onChange={(e) => setValor(e.target.value)} placeholder={somaSaldos.toFixed(2)} />
              </Campo>
            )}
            <Campo label="Prazo (parcelas)">
              <input style={inputStyle} inputMode="numeric" value={prazo} onChange={(e) => setPrazo(e.target.value.replace(/\D/g, ""))} placeholder="Ex.: 72" />
            </Campo>
            {exigeSenha ? (
              <Campo label="Senha de transação">
                <input style={inputStyle} type="password" autoComplete="one-time-code" value={senha} onChange={(e) => setSenha(e.target.value)} placeholder="Senha de transação" />
              </Campo>
            ) : null}
          </div>
          <p style={{ margin: 0, fontSize: 12, color: "var(--text-muted)" }}>
            Se a instituição aceitar exatamente o que você pediu, o contrato já sai aprovado — por isso o pedido é confirmado com a
            sua senha. Qualquer mudança da instituição volta para você aceitar.
          </p>
          {mut.error ? (
            <div role="alert" style={{ color: "var(--danger, #DC2626)", fontSize: ".9rem" }}>
              {mensagemDeErro(mut.error, "Não foi possível enviar o pedido.")}
            </div>
          ) : null}
          <ImpersonateReadOnlyNotice acao="Pedir refinanciamento" />
          <div>
            <Button onClick={() => { setOk(null); mut.mutate(); }} disabled={!formularioOk || mut.isPending || impersonando}>
              {mut.isPending ? "Enviando…" : "Enviar pedido à instituição"}
            </Button>
          </div>
        </div>
      ) : null}
    </Card>
  );
}

function RefinCard({
  r,
  exigeSenha,
  impersonando,
  onMudou,
}: {
  r: Refinanciamento;
  exigeSenha: boolean;
  impersonando: boolean;
  onMudou: () => void;
}) {
  const acoes = acoesDoRefin(r, "servidor");
  const [senha, setSenha] = useState("");
  const [ciente, setCiente] = useState(false);
  const aceitar = useMutation({ mutationFn: () => atlas.refinanciamento.aceitar(r.id, exigeSenha ? senha : undefined), onSuccess: onMudou });
  const recusar = useMutation({ mutationFn: () => atlas.refinanciamento.recusar(r.id), onSuccess: onMudou });
  const cancelar = useMutation({ mutationFn: () => atlas.refinanciamento.cancelarPedido(r.id), onSuccess: onMudou });
  const erro = aceitar.error ?? recusar.error ?? cancelar.error;
  const ocupado = aceitar.isPending || recusar.isPending || cancelar.isPending;
  const p = r.proposta;

  return (
    <Card style={{ borderColor: r.estado === "ofertado" ? "var(--gold-500)" : undefined }}>
      <div style={{ display: "flex", justifyContent: "space-between", gap: 8, flexWrap: "wrap" }}>
        <div>
          <div style={{ fontWeight: 700 }}>{r.bancoNome}</div>
          <div style={{ fontSize: 12, color: "var(--text-muted)", fontFamily: "var(--font-mono)" }}>
            {r.id} · {r.iniciadoPor === "banco" ? "oferta da instituição" : "seu pedido"} · {fmtDataHora(r.criadoEm)}
          </div>
        </div>
        <Pill variant={pillDoEstadoRefin(r.estado)}>{ESTADO_REFIN_LABEL[r.estado]}</Pill>
      </div>

      <div style={{ marginTop: 10, fontSize: 13, color: "var(--text-muted)" }}>
        Contratos: {r.origens.map((o) => `${o.adf} (${fmtBRL(o.parcela)})`).join(", ")} · parcelas atuais {fmtBRL(r.parcelasOrigem)}
      </div>
      {r.pedido ? (
        <div style={{ marginTop: 4, fontSize: 13, color: "var(--text-muted)" }}>
          Pedido: {r.pedido.parcela != null ? `parcela ${fmtBRL(r.pedido.parcela)}` : ""}
          {r.pedido.valor != null ? `valor ${fmtBRL(r.pedido.valor)}` : ""}
          {r.pedido.prazo != null ? ` em ${r.pedido.prazo}x` : ""}
        </div>
      ) : null}

      {p ? (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(130px, 1fr))", gap: 10, marginTop: 12, fontSize: 13 }}>
          <KV label="Nova parcela" v={fmtBRL(p.valorParcela)} accent />
          <KV label="Prazo" v={`${p.prazo}x`} />
          <KV label="Valor financiado" v={fmtBRL(p.valorFinanciado)} />
          <KV label="Taxa" v={fmtTaxaFracao(p.taxaAm)} />
          <KV label="CET" v={fmtTaxaFracao(p.cetAm)} />
          <KV label="IOF" v={fmtBRL(p.iof)} />
          <KV label="Troco na conta" v={fmtBRL(p.troco)} accent={p.troco > 0} />
          {r.diferencaParcela != null ? (
            <KV
              label="Diferença na parcela"
              v={`${r.diferencaParcela > 0 ? "+" : r.diferencaParcela < 0 ? "−" : ""} ${fmtBRL(Math.abs(r.diferencaParcela))}`}
            />
          ) : null}
        </div>
      ) : null}

      {r.estado === "ofertado" && r.expiraEm ? (
        <div style={{ marginTop: 10, fontSize: 12, color: "var(--gold-500)" }}>Proposta válida até {fmtDataHora(r.expiraEm)}.</div>
      ) : null}
      {r.sucessor ? (
        <div style={{ marginTop: 10, fontSize: 13 }}>
          Contrato novo <b style={{ fontFamily: "var(--font-mono)" }}>{r.sucessor.adf}</b> — {r.sucessor.situacao}.
          {r.estado === "aceito" ? " Os contratos atuais saem da folha quando ele entrar." : ""}
        </div>
      ) : null}
      {r.motivo ? <div style={{ marginTop: 8, fontSize: 13, color: "var(--text-muted)" }}>Motivo: {r.motivo}</div> : null}

      {acoes.aceitar ? (
        <div style={{ marginTop: 14, display: "grid", gap: 10 }}>
          <label style={{ display: "flex", gap: 8, alignItems: "center", fontSize: ".9rem" }}>
            <input type="checkbox" checked={ciente} onChange={(e) => setCiente(e.target.checked)} disabled={impersonando} />
            Li as condições e autorizo o contrato novo, que quita os contratos acima.
          </label>
          {exigeSenha ? (
            <input
              style={{ ...inputStyle, maxWidth: 280 }}
              type="password"
              autoComplete="one-time-code"
              value={senha}
              onChange={(e) => setSenha(e.target.value)}
              placeholder="Senha de transação"
              aria-label="Senha de transação"
            />
          ) : null}
        </div>
      ) : null}

      {erro ? (
        <div role="alert" style={{ marginTop: 10, color: "var(--danger, #DC2626)", fontSize: ".9rem" }}>
          {mensagemDeErro(erro, "Não foi possível concluir.")}
        </div>
      ) : null}

      {acoes.aceitar || acoes.recusar || acoes.cancelar ? (
        <div style={{ marginTop: 14, display: "flex", gap: 8, flexWrap: "wrap" }}>
          {acoes.aceitar ? (
            <Button onClick={() => aceitar.mutate()} disabled={ocupado || impersonando || !ciente || (exigeSenha && !senha)}>
              {aceitar.isPending ? "Aceitando…" : "Aceitar proposta"}
            </Button>
          ) : null}
          {acoes.recusar ? (
            <Button variant="ghost" onClick={() => recusar.mutate()} disabled={ocupado || impersonando}>
              Recusar
            </Button>
          ) : null}
          {acoes.cancelar ? (
            <Button variant="ghost" onClick={() => cancelar.mutate()} disabled={ocupado || impersonando}>
              Cancelar pedido
            </Button>
          ) : null}
        </div>
      ) : null}
    </Card>
  );
}

function Campo({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label style={{ display: "grid", gap: 6 }}>
      <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: ".06em", color: "var(--text-dim)", textTransform: "uppercase" }}>{label}</span>
      {children}
    </label>
  );
}

function KV({ label, v, accent }: { label: string; v: string; accent?: boolean }) {
  return (
    <div>
      <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.06em", color: "var(--text-dim)", textTransform: "uppercase" }}>{label}</div>
      <div style={{ marginTop: 4, color: accent ? "var(--emerald-500)" : "var(--text)", fontWeight: accent ? 700 : 500 }}>{v}</div>
    </div>
  );
}
